import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Heart, GraduationCap, Users, Briefcase, Brain, Phone, MapPin, ChevronRight, Instagram, Facebook, Mail } from 'lucide-react';

const teamImages = [
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_1.jpg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_2.jpg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/foto_inicio.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_4.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_5.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_6.jpeg",
];

const inspirationImages = [
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_1.jpg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_2.jpg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_3.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_4.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_5.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_6.jpeg",
];

const donationImages = [
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_1.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_2.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_3.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_4.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_5.jpeg",
  "https://jessica-ong.s3.us-east-1.amazonaws.com/landing/donacion_6.jpeg",
];

const Home = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const programs = [
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: 'Apoyo Educativo',
      description: 'Brindamos uniformes, kits escolares y apoyo integral para niños, niñas y jóvenes en su educación básica y técnica.',
      color: '#59C1D9'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Apoyo Universitario',
      description: 'Becas parciales, apoyo tecnológico, transporte y alimentación para estudiantes universitarios con excelencia académica.',
      color: '#D991C0'
    },
    {
      icon: <Briefcase className="w-12 h-12" />,
      title: 'Emprendimientos',
      description: 'Recursos, mentoría y financiación para aspirantes a emprendedores, fomentando la innovación y el desarrollo sostenible.',
      color: '#F2D541'
    },
    {
      icon: <Brain className="w-12 h-12" />,
      title: 'Acompañamiento Psicosocial',
      description: 'Cuidamos la salud emocional con apoyo, orientación y herramientas para fortalecer capacidades y promover resiliencia.',
      color: '#59C1D9'
    }
  ];

  const stats = [
    { number: '10+', label: 'Años de Experiencia' },
    { number: '500+', label: 'Beneficiarios' },
    { number: '4', label: 'Programas Activos' },
  ];

  const handleDonateClick = () => {
    window.open('https://www.paypal.com/donate/?hosted_button_id=T3JQLNKRQ3D78', '_blank');
  };

  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/fundacionjessica', '_blank');
  };

  const handleFacebookClick = () => {
    window.open('https://web.facebook.com/JessicaFoundation1', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-6'
        }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/logo.jpeg"
                alt="Fundación Jessica"
                className="h-12 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#inicio" className="text-[#262624] hover:text-[#D991C0] transition-colors duration-200 font-medium">Inicio</a>
              <a href="#nosotros" className="text-[#262624] hover:text-[#D991C0] transition-colors duration-200 font-medium">Nosotros</a>
              <a href="#programas" className="text-[#262624] hover:text-[#D991C0] transition-colors duration-200 font-medium">Programas</a>
              <a href="#contacto" className="text-[#262624] hover:text-[#D991C0] transition-colors duration-200 font-medium">Contacto</a>
            </nav>
            <Button
              onClick={handleDonateClick}
              className="bg-[#D991C0] hover:bg-[#c77aad] text-white font-semibold px-6 py-2 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <Heart className="w-4 h-4 mr-2" />
              Donar Ahora
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f7f7f7] via-white to-[#f7f7f7]"></div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#59C1D9] opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#D991C0] opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-[#F2D541] opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-block">
                <span className="text-[#59C1D9] font-semibold text-lg tracking-wide">Fundación Jessica</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-[#262624] leading-tight">
                Pequeñas acciones, <span className="text-[#D991C0]">grandes cambios</span>. Juntos construimos oportunidades.
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Transformamos vidas a través de la educación, el apoyo emocional y las oportunidades. Cada donación construye un futuro mejor para nuestra comunidad.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleDonateClick}
                  size="lg"
                  className="bg-[#D991C0] hover:bg-[#c77aad] text-white font-bold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-xl group"
                >
                  Dona Ahora
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => document.getElementById('programas').scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#59C1D9] text-[#59C1D9] hover:bg-[#59C1D9] hover:text-white font-bold text-lg px-8 py-6 transition-all duration-300"
                >
                  Conoce Más
                </Button>
              </div>

              {/* Stats */}
              {/* <div className="grid grid-cols-3 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-[#D991C0]">{stat.number}</div>
                    <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div> */}
            </div>

            {/* Main Image */}
            <div className="relative group">
              {/* Glow effect background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D991C0] via-[#59C1D9] to-[#F2D541] rounded-full opacity-20 blur-3xl animate-pulse"></div>

              {/* Image container with modern effects */}
              <div className="relative transform group-hover:scale-105 transition-all duration-500">
                <div className="relative rounded-3xl overflow-visible">
                  {/* Decorative floating shapes */}
                  <div className="absolute -top-6 -right-6 w-20 h-20 bg-[#F2D541] rounded-full opacity-30 blur-xl"></div>
                  <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-[#D991C0] rounded-full opacity-30 blur-xl"></div>

                  {/* Main image wrapper with gradient border */}
                  <div className="relative">
                    {/* Gradient border effect */}
                    <div className="absolute -inset-1 bg-gradient-to-br from-[#D991C0] via-[#59C1D9] to-[#F2D541] rounded-3xl opacity-40 blur-sm"></div>

                    {/* Image container */}
                    <div className="relative bg-white p-3 rounded-3xl shadow-2xl">
                      <div className="relative overflow-hidden rounded-2xl">
                        <img
                          // src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/foto_inicio.jpeg"
                          src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/equipo_3.jpg"
                          alt="Fundación Jessica - Transformando vidas"
                          className="w-full h-auto object-cover"
                          style={{
                            filter: 'brightness(1.05) contrast(1.05) drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1))'
                          }}
                        />

                        {/* Overlay gradient for better integration */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#D991C0] to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                      </div>
                    </div>

                    {/* Floating badge - positioned outside to be visible */}
                    {/* <div className="absolute -bottom-8 -right-8 z-10 bg-gradient-to-br from-[#D991C0] to-[#59C1D9] text-white px-6 py-4 rounded-2xl shadow-2xl transform rotate-3 group-hover:rotate-6 group-hover:scale-110 transition-all duration-300">
                      <div className="flex items-center space-x-2 mb-1">
                        <Heart className="w-5 h-5 fill-white animate-pulse" />
                        <span className="font-bold text-lg">10+ años</span>
                      </div>
                      <p className="text-xs opacity-90 font-medium">transformando vidas</p>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Section 1 - After Hero */}
      <section className="py-16 bg-gradient-to-b from-white to-[#f7f7f7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/inspiracion_1.jpg"
                alt="Fundación Jessica - Nuestro impacto"
                className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#262624] via-transparent to-transparent opacity-60"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <h3 className="text-3xl font-bold mb-2">Transformando Vidas</h3>
                <p className="text-lg opacity-90">Cada día, cada historia, cada sonrisa</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-24 bg-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F2D541] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#59C1D9] opacity-5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-fade-in">
            <span className="text-[#59C1D9] font-semibold text-lg tracking-wide">Nuestra Historia</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#262624] mt-4">¿Quiénes Somos?</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#D991C0] to-[#59C1D9] mx-auto mt-4"></div>
          </div>

          {/* Founders Story with creative layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#D991C0] to-[#59C1D9] rounded-3xl opacity-10 blur-xl group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative space-y-6 bg-white rounded-2xl p-8 shadow-xl">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D991C0] to-[#59C1D9] flex items-center justify-center">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <div className="h-1 flex-1 bg-gradient-to-r from-[#D991C0] to-transparent"></div>
                </div>

                <p className="text-gray-700 leading-relaxed text-lg">
                  La Fundación Jessica fue fundada por dos hermanas Manizalitas, <strong className="text-[#D991C0]">Ana Pastora Betancur García</strong> y <strong className="text-[#D991C0]">Beatriz A. Betancur García</strong>, que viniendo de una familia humilde y viendo las diferentes necesidades de la comunidad se unieron pensando en mejorar de alguna manera la calidad de vida de la comunidad.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Cuando comenzamos este viaje nuestra visión era clara: tener un impacto positivo en nuestra comunidad. Como fundadores, reconocemos los desafíos que enfrentan muchas personas para acceder a recursos y oportunidades. Queríamos cerrar esa brecha.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Creemos que todos merecen la oportunidad de perseguir sus sueños, independientemente de su origen. Al crear esta fundación, nuestro objetivo es brindar las herramientas, las conexiones y el apoyo necesarios para que las personas prosperen.
                </p>
              </div>
            </div>

            {/* Image placeholder with creative design */}
            {/* <div className="relative h-full min-h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#59C1D9] via-[#D991C0] to-[#F2D541] rounded-3xl opacity-20 blur-2xl animate-pulse"></div>
              <div className="relative h-full bg-white rounded-3xl shadow-2xl p-8 flex items-center justify-center border-4 border-transparent hover:border-[#D991C0] transition-all duration-500">
                <div className="text-center space-y-6">
                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#59C1D9] to-[#D991C0] rounded-full blur-xl opacity-50"></div>
                    <div className="relative w-40 h-40 mx-auto bg-gradient-to-br from-[#59C1D9] to-[#D991C0] rounded-full flex items-center justify-center">
                      <Users className="w-20 h-20 text-white" />
                    </div>
                  </div>
                  <p className="text-gray-500 font-semibold text-lg">Espacio para imagen</p>
                  <p className="text-sm text-gray-400">Fundadoras o equipo</p>
                </div>
              </div>
            </div> */}
            <div className="relative bg-white p-3 rounded-3xl shadow-2xl">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/quienes_somos.jpeg"
                  alt="Fundación Jessica - Transformando vidas"
                  className="w-full h-auto object-cover"
                  style={{
                    filter: 'brightness(1.05) contrast(1.05) drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1))'
                  }}
                />

                {/* Overlay gradient for better integration */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#D991C0] to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
              </div>
            </div>
          </div>

          {/* Mission & Vision with animated cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#D991C0] to-[#F2D541] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur"></div>
              <Card className="relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#D991C0] to-[#F2D541]"></div>
                <CardContent className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#D991C0] bg-opacity-20 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                      <GraduationCap className="w-8 h-8 text-[#D991C0]" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#D991C0]">Misión</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    Ayudar a nuestros beneficiarios a alcanzar sus metas de estudio y/o mejorar sus condiciones de vida. Apoyamos a personas que se encuentren estudiando en la ciudad de Manizales, principalmente oriundos de Manizales o de municipios vecinos, incluyendo madres cabeza de familia.
                  </p>
                  <div className="mt-6 flex items-center space-x-2 text-[#D991C0] font-semibold">
                    <span>Nuestro compromiso</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#59C1D9] to-[#D991C0] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur"></div>
              <Card className="relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#59C1D9] to-[#D991C0]"></div>
                <CardContent className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#59C1D9] bg-opacity-20 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                      <Heart className="w-8 h-8 text-[#59C1D9]" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#59C1D9]">Visión</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    Vemos la Fundación Jessica como líder en Manizales, Caldas (Colombia), en el apoyo a niños, niñas y jóvenes en aspectos que tengan que ver con su educación y desarrollo, incidiendo positivamente en el mejoramiento de su calidad de vida.
                  </p>
                  <div className="mt-6 flex items-center space-x-2 text-[#59C1D9] font-semibold">
                    <span>Nuestro futuro</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Team section with creative design */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#59C1D9] via-[#D991C0] to-[#F2D541] rounded-3xl opacity-10"></div>
            <Card className="relative border-0 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#59C1D9] via-[#D991C0] to-[#F2D541]"></div>
              <CardContent className="p-10">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-8">
                  <div className="mb-6 md:mb-0">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#59C1D9] to-[#D991C0] flex items-center justify-center shadow-lg transform hover:scale-110 hover:rotate-6 transition-all duration-500">
                      <Users className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-[#262624] mb-4 flex items-center">
                      Equipo de Trabajo y Voluntarios
                      <div className="ml-4 flex-1 h-1 bg-gradient-to-r from-[#D991C0] to-transparent max-w-xs"></div>
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      El personal y los voluntarios de la Fundación son el corazón y el alma de nuestra misión. Su dedicación, experiencia y pasión impulsan nuestras iniciativas. Cada miembro del equipo aporta habilidades y perspectivas únicas, lo que fomenta un entorno colaborativo que fomenta la innovación y la creatividad. Trabajan incansablemente para apoyar a nuestra comunidad, construir relaciones significativas y garantizar que nuestros programas tengan un impacto duradero.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Carousel 2 - After Team Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f7f7f7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-[#262624]">Nuestro Equipo en Acción</h3>
            <p className="text-gray-600 mt-2">Comprometidos con cada beneficiario</p>
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="gallery-carousel overflow-hidden rounded-3xl shadow-2xl">
              <div className="gallery-track flex animate-scroll">
                {teamImages.map((image, index) => (
                  <div key={index} className="gallery-item flex-shrink-0 w-[380px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={image}
                        alt={`Equipo Fundación Jessica ${index + 1}`}
                        className="w-full h-[280px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#D991C0] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                    </div>
                  </div>
                ))}
                {teamImages.map((image, index) => (
                  <div key={`dup-${index}`} className="gallery-item flex-shrink-0 w-[380px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={image}
                        alt={`Equipo Fundación Jessica ${index + 1}`}
                        className="w-full h-[280px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#D991C0] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Section 2 - After About */}
      {/* <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/WhatsApp+Image+2025-11-11+at+9.57.56+AM.jpeg"
                alt="Fundación Jessica - Educación"
                className="w-full h-[350px] object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-[#D991C0] opacity-20 group-hover:opacity-30 transition-opacity"></div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/WhatsApp+Image+2025-11-11+at+9.57.56+AM+(2).jpeg"
                alt="Fundación Jessica - Apoyo"
                className="w-full h-[350px] object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-[#59C1D9] opacity-20 group-hover:opacity-30 transition-opacity"></div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Programs Section */}
      <section id="programas" className="py-24 bg-gradient-to-br from-[#f7f7f7] via-white to-[#f7f7f7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D991C0] font-semibold text-lg tracking-wide">Nuestros Servicios</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#262624] mt-4">Qué Hacemos</h2>
            <p className="text-gray-600 mt-4 text-lg max-w-2xl mx-auto">
              Desarrollamos programas integrales que transforman vidas y construyen futuro
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {programs.map((program, index) => (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white"
              >
                <CardContent className="p-8">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: `${program.color}20`, color: program.color }}
                  >
                    {program.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#262624] mb-3">{program.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{program.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Programs */}
          <div className="mt-16 grid lg:grid-cols-2 gap-8">
            <Card className="shadow-xl border-0 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-[#59C1D9] to-[#D991C0]"></div>
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#59C1D9] bg-opacity-20 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-[#59C1D9]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#262624]">Programa de Apoyo Educativo</h3>
                </div>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kits Escolares:</strong> Ofrecemos kits escolares llenos de útiles necesarios para niños.</p>
                  <p><strong>Apoyo a Cursos Técnicos:</strong> Los jóvenes reciben ayuda con la matrícula, subsidios de transporte y kits escolares.</p>
                  <p><strong>Validación de Estudios:</strong> Los estudiantes que validan su Escuela Primaria y Secundaria reciben cobertura para la matrícula.</p>
                  <p><strong>Alcance Comunitario:</strong> Enviamos kits escolares a comunidades en la Amazonía y aceptamos donaciones de computadoras portátiles.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-0 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-[#D991C0] to-[#F2D541]"></div>
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#D991C0] bg-opacity-20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#D991C0]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#262624]">Apoyo a Estudiantes Universitarios</h3>
                </div>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Universidades Privadas:</strong> Ofrecemos becas que cubren parcialmente los costos de matrícula.</p>
                  <p><strong>Universidades Públicas:</strong> Brindamos apoyo que puede incluir comidas diarias, asistencia con la vivienda, ayuda con el transporte.</p>
                  <p><strong>Apoyo Tecnológico:</strong> Otorgamos computadoras nuevas a estudiantes con excelente desempeño académico.</p>
                  <p><strong>Donaciones:</strong> Aceptamos donaciones de computadoras portátiles usadas en buen estado.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D991C0] font-semibold text-lg tracking-wide">Nuestra Galería</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#262624] mt-4">Momentos que Inspiran</h2>
            <p className="text-gray-600 mt-4 text-lg max-w-2xl mx-auto">
              Cada fotografía cuenta una historia de esperanza, superación y transformación
            </p>
          </div>

          {/* Carousel Gallery */}
          <div className="relative max-w-7xl mx-auto">
            <div className="gallery-carousel overflow-hidden rounded-3xl shadow-2xl">
              <div className="gallery-track flex animate-scroll">
                {inspirationImages.map((src, index) => (
                  <div key={index} className="gallery-item flex-shrink-0 w-[400px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={src}
                        alt={`Fundación Jessica - Galería ${index + 1}`}
                        className="w-full h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#262624] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <p className="font-semibold text-sm">Momento de Impacto #{index + 1}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Duplicate for seamless loop */}
                {inspirationImages.map((src, index) => (
                  <div key={`dup-${index}`} className="gallery-item flex-shrink-0 w-[400px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={src}
                        alt={`Fundación Jessica - Galería ${index + 1}`}
                        className="w-full h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#262624] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <p className="font-semibold text-sm">Momento de Impacto #{index + 1}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Donate Section */}
      <section className="py-24 bg-gradient-to-r from-[#D991C0] to-[#59C1D9] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Tu Donación Transforma Vidas
            </h2>
            <p className="text-xl text-white text-opacity-90 leading-relaxed">
              Cada aporte, sin importar su tamaño, marca la diferencia. Ayúdanos a seguir construyendo sueños y oportunidades para nuestra comunidad.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                onClick={handleDonateClick}
                size="lg"
                className="bg-white text-[#D991C0] hover:bg-gray-100 font-bold text-xl px-12 py-7 transition-all duration-300 hover:scale-110 shadow-2xl group"
              >
                <Heart className="w-6 h-6 mr-3 group-hover:animate-pulse" />
                Hacer una Donación
                <ChevronRight className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform" />
              </Button>
            </div>
            <p className="text-white text-opacity-75 text-sm">Donaciones seguras a través de PayPal</p>
          </div>
        </div>
      </section>

      {/* Photo Section 4 - Before FAQ */}
      {/* <section className="py-16 bg-gradient-to-b from-[#f7f7f7] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/WhatsApp+Image+2025-11-11+at+9.59.38+AM.jpeg"
                alt="Fundación Jessica"
                className="w-full h-[280px] object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-[#F2D541] opacity-10"></div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/WhatsApp+Image+2025-11-11+at+9.59.38+AM+(1).jpeg"
                alt="Fundación Jessica"
                className="w-full h-[280px] object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-[#D991C0] opacity-10"></div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/WhatsApp+Image+2025-11-11+at+10.01.14+AM+(4).jpeg"
                alt="Fundación Jessica"
                className="w-full h-[280px] object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-[#59C1D9] opacity-10"></div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Gallery Carousel 3 - After Donate CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-[#262624]">Historias de Éxito</h3>
            <p className="text-gray-600 mt-2">Cada donación genera un impacto real</p>
          </div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="gallery-carousel overflow-hidden rounded-3xl shadow-2xl">
              <div className="gallery-track-reverse flex animate-scroll-reverse">
                {donationImages.map((image, index) => (
                  <div key={index} className="gallery-item flex-shrink-0 w-[400px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={image}
                        alt={`Historias de Éxito ${index + 1}`}
                        className="w-full h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#59C1D9] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                    </div>
                  </div>
                ))}
                {donationImages.map((image, index) => (
                  <div key={`dup-${index}`} className="gallery-item flex-shrink-0 w-[400px] px-3">
                    <div className="relative rounded-2xl overflow-hidden group">
                      <img 
                        src={image}
                        alt={`Historias de Éxito ${index + 1}`}
                        className="w-full h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#59C1D9] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-[#59C1D9] font-semibold text-lg tracking-wide">Preguntas Frecuentes</span>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#262624] mt-4">¿Tienes Dudas?</h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-2 border-gray-100 rounded-lg px-6 hover:border-[#D991C0] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#262624] hover:text-[#D991C0] hover:no-underline">
                  ¿Cómo puedo ser beneficiario de la fundación?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed">
                  Para ser beneficiario, debes estar estudiando en Manizales en universidades, instituciones tecnológicas o colegios públicos. Principalmente atendemos a personas oriundas de Manizales o municipios vecinos. Contáctanos a través de WhatsApp para más información sobre los requisitos específicos.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-2 border-gray-100 rounded-lg px-6 hover:border-[#D991C0] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#262624] hover:text-[#D991C0] hover:no-underline">
                  ¿Mi donación es deducible de impuestos?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed">
                  Sí, la Fundación Jessica está legalmente constituida y todas las donaciones son deducibles de impuestos según la normativa colombiana. Te proporcionaremos el certificado correspondiente.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-2 border-gray-100 rounded-lg px-6 hover:border-[#D991C0] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#262624] hover:text-[#D991C0] hover:no-underline">
                  ¿Cómo puedo ser voluntario?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed">
                  Siempre buscamos personas comprometidas para unirse a nuestro equipo de voluntarios. Contáctanos a través de WhatsApp o completa el formulario de contacto indicando tu interés en ser voluntario y tus áreas de especialización.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-2 border-gray-100 rounded-lg px-6 hover:border-[#D991C0] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#262624] hover:text-[#D991C0] hover:no-underline">
                  ¿Aceptan donaciones en especie?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed">
                  Sí, aceptamos donaciones de computadoras portátiles en buen estado, útiles escolares y otros elementos que puedan beneficiar a nuestros estudiantes. Contáctanos para coordinar la entrega.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 bg-gradient-to-br from-[#f7f7f7] via-white to-[#f7f7f7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D991C0] font-semibold text-lg tracking-wide">Contáctanos</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#262624] mt-4">Estamos Aquí Para Ayudarte</h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#D991C0] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#D991C0]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#262624] mb-2">Ubicación</h3>
                        <p className="text-gray-700">Manizales, Caldas<br />Colombia</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#262624] mb-4">Síguenos en Redes Sociales</h3>
                    <div className="flex space-x-4">
                      <button
                        onClick={handleInstagramClick}
                        className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E1306C] to-[#F77737] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 shadow-lg"
                        aria-label="Instagram"
                      >
                        <Instagram className="w-7 h-7" />
                      </button>
                      <button
                        onClick={handleFacebookClick}
                        className="w-14 h-14 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 shadow-lg"
                        aria-label="Facebook"
                      >
                        <Facebook className="w-7 h-7" />
                      </button>
                    </div>
                    <p className="text-gray-600 text-sm mt-4">
                      Mantente al día con nuestras actividades y conoce más historias de impacto
                    </p>
                  </CardContent>
                </Card>

                <div className="bg-gradient-to-br from-[#59C1D9] to-[#D991C0] rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-3">¿Listo para hacer la diferencia?</h3>
                  <p className="text-white text-opacity-90 mb-6">
                    Contáctanos hoy y descubre cómo puedes ser parte del cambio en nuestra comunidad.
                  </p>
                  <div className="flex space-x-3">
                    <Button 
                      onClick={handleInstagramClick}
                      className="bg-white text-[#D991C0] hover:bg-gray-100 font-bold flex-1"
                    >
                      <Instagram className="w-4 h-4 mr-2" />
                      Instagram
                    </Button>
                    <Button 
                      onClick={handleFacebookClick}
                      className="bg-white text-[#D991C0] hover:bg-gray-100 font-bold flex-1"
                    >
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </Button>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <Card className="shadow-xl border-0">
                <CardContent className="p-8">
                  <form className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#262624] mb-2">Nombre Completo</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D991C0] focus:outline-none transition-colors"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#262624] mb-2">Correo Electrónico</label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D991C0] focus:outline-none transition-colors"
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#262624] mb-2">Teléfono</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D991C0] focus:outline-none transition-colors"
                        placeholder="+57 XXX XXX XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#262624] mb-2">Mensaje</label>
                      <textarea
                        rows="4"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D991C0] focus:outline-none transition-colors resize-none"
                        placeholder="¿En qué podemos ayudarte?"
                      ></textarea>
                    </div>
                    <Button
                      type="button"
                      className="w-full bg-[#D991C0] hover:bg-[#c77aad] text-white font-bold py-3 transition-all duration-300 hover:scale-105"
                    >
                      Enviar Mensaje
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#262624] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            <div className="space-y-4">
              <img
                src="https://jessica-ong.s3.us-east-1.amazonaws.com/landing/logo.jpeg"
                alt="Fundación Jessica"
                className="h-20 w-auto"
              />
              <p className="text-gray-300 text-sm leading-relaxed">
                Pequeñas acciones, grandes cambios
              </p>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-[#D991C0]">Enlaces Rápidos</h3>
              <ul className="space-y-2">
                <li><a href="#inicio" className="text-gray-300 hover:text-[#D991C0] transition-colors">Inicio</a></li>
                <li><a href="#nosotros" className="text-gray-300 hover:text-[#D991C0] transition-colors">Nosotros</a></li>
                <li><a href="#programas" className="text-gray-300 hover:text-[#D991C0] transition-colors">Programas</a></li>
                <li><a href="#contacto" className="text-gray-300 hover:text-[#D991C0] transition-colors">Contacto</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-[#59C1D9]">Contacto</h3>
              <ul className="space-y-3 text-gray-300 text-sm mb-6">
                <li className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Manizales, Caldas, Colombia</span>
                </li>
              </ul>
              <h4 className="font-semibold text-sm mb-3 text-[#D991C0]">Síguenos</h4>
              <div className="flex space-x-3">
                <button
                  onClick={handleInstagramClick}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E1306C] to-[#F77737] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </button>
                <button
                  onClick={handleFacebookClick}
                  className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 Fundación Jessica. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;